'use client';

import * as React from 'react';
import { MutableRefObject, useEffect, useState } from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from '@ais-ui/utils';

type ScrollAreaProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Root
> & {
  infiniteScrollRef?: React.Ref<HTMLDivElement>;
  observeResize?: boolean;
};

const smoothScrollBy = (
  element: HTMLElement,
  distance: number,
  duration: number
) => {
  const start = element.scrollTop;
  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Easing function: easeInOutQuad
    const ease =
      progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;

    element.scrollTop = start + distance * ease;

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

const useMousePosition = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    let mouseX = 0;
    let mouseY = 0;

    const updateMousePosition = (ev: MouseEvent) => {
      mouseX = ev.clientX;
      mouseY = ev.clientY;
    };

    window.addEventListener('mousemove', updateMousePosition);

    const intervalId = setInterval(() => {
      setMousePosition({ x: mouseX, y: mouseY });
    }, 100);

    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      clearInterval(intervalId);
    };
  }, []);

  return mousePosition;
};

const useResizeObserver = (
  ref: MutableRefObject<HTMLElement | null>,
  childrenCount: number,
  children: React.ReactNode,
  isActive: boolean
) => {
  const { y: mouseY } = useMousePosition();
  const [previousChildrenCount, setPrevChildrenCount] = useState(0);
  const [previousScrollHeight, setPrevScrollHeight] = useState(0);
  const clickedElementRef = React.useRef<HTMLElement | null>(null);
  const clickedPositionRef = React.useRef(0);
  const element = ref.current;

  useEffect(() => {
    const handleClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement;

      // Store the clicked element and its position
      clickedElementRef.current = target;

      // Get the position relative to the viewport
      const elementRect = element?.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();

      if (elementRect) {
        // Store the click position relative to the scrollable container
        clickedPositionRef.current =
          targetRect.top - elementRect.top + (element?.scrollTop || 0);
      }
    };

    if (element) {
      setPrevChildrenCount(childrenCount);
      setPrevScrollHeight(element.scrollHeight);
      element.addEventListener('click', handleClick, true); // Use capture phase
    }

    return () => element?.removeEventListener('click', handleClick, true);
  }, [childrenCount, element]);

  useEffect(() => {
    if (!isActive || !ref.current) return;

    const observer = new MutationObserver((mutations) => {
      // Check if any mutation is related to accordion expansion
      const hasAccordionChange = mutations.some((mutation) => {
        const target = mutation.target as HTMLElement;
        return (
          (mutation.type === 'attributes' &&
            (mutation.attributeName === 'data-state' ||
              mutation.attributeName === 'aria-expanded' ||
              mutation.attributeName === 'open')) ||
          (mutation.type === 'childList' &&
            target.closest('[data-radix-accordion], [role="region"], details'))
        );
      });

      // Use shorter delay for faster response
      const delay = hasAccordionChange ? 200 : 50;

      setTimeout(() => {
        if (!element || !clickedElementRef.current) return;

        const { scrollHeight, scrollTop } = element;
        if (previousScrollHeight === scrollHeight) return;

        if (previousChildrenCount === 0 || previousScrollHeight === 0) {
          setPrevChildrenCount(childrenCount);
          setPrevScrollHeight(scrollHeight);
          return;
        }

        const heightDifference = scrollHeight - previousScrollHeight;

        // Skip if new children were added (not an expansion)
        if (childrenCount > previousChildrenCount) {
          setPrevChildrenCount(childrenCount);
          setPrevScrollHeight(scrollHeight);
          return;
        }

        // Only adjust scroll if content expanded (height increased)
        if (heightDifference > 0) {
          const elementRect = element.getBoundingClientRect();
          const viewportHeight = elementRect.height;

          // Find the expanded content
          let expandedContent: HTMLElement | null = null;

          // Try to find accordion content or details element
          const accordionContent = clickedElementRef.current
            .closest('[data-state="open"]')
            ?.querySelector(
              '[data-radix-accordion-content], [role="region"]'
            ) as HTMLElement;
          const detailsContent = clickedElementRef.current.closest(
            'details[open]'
          ) as HTMLElement;

          expandedContent = accordionContent || detailsContent;

          if (expandedContent) {
            const contentRect = expandedContent.getBoundingClientRect();
            const contentHeight = contentRect.height;
            const contentTop = contentRect.top;
            const contentBottom = contentRect.bottom;
            const viewportTop = elementRect.top;
            const viewportBottom = elementRect.bottom;

            // Extra scroll padding - this will scroll BEYOND just showing the content
            const extraScroll = 80; // Scroll this much extra beyond the bottom

            // Check if content bottom is below viewport or close to bottom
            if (contentBottom > viewportBottom - 100) {
              // Calculate how much we need to scroll to:
              // 1. Show the entire bottom of content
              // 2. PLUS add extra space beyond
              const scrollNeeded = contentBottom - viewportBottom + extraScroll;

              // But don't scroll so much that we hide the top of content
              const maxScroll = contentTop - viewportTop - 20; // Keep at least 20px of top visible

              if (scrollNeeded < maxScroll || contentHeight > viewportHeight) {
                // Safe to scroll the full amount
                smoothScrollBy(element, scrollNeeded, 250);
              } else if (maxScroll > 0) {
                // Scroll as much as we can without hiding the top
                smoothScrollBy(element, maxScroll, 250);
              } else {
                // Content fits, try to center it with bias toward showing bottom
                const idealScroll =
                  contentBottom - viewportBottom + viewportHeight * 0.2;
                smoothScrollBy(element, idealScroll, 250);
              }
            }
            // If content top is above viewport
            else if (contentTop < viewportTop) {
              const scrollAmount = contentTop - viewportTop - extraScroll;
              smoothScrollBy(element, scrollAmount, 250);
            }
          } else {
            // Fallback to original logic with extra padding
            const currentClickPosition = clickedPositionRef.current;
            const visibleBottom = scrollTop + viewportHeight;
            const expandedBottom = currentClickPosition + heightDifference;

            if (expandedBottom > visibleBottom - 100) {
              const scrollAmount = expandedBottom - visibleBottom + 80;
              smoothScrollBy(element, scrollAmount, 250);
            }
          }
        }

        setPrevScrollHeight(scrollHeight);
        clickedElementRef.current = null;
      }, delay);
    });

    observer.observe(ref.current, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['data-state', 'aria-expanded', 'open', 'style'],
    });

    return () => observer.disconnect();
  }, [
    childrenCount,
    isActive,
    element,
    mouseY,
    previousChildrenCount,
    previousScrollHeight,
    ref,
    setPrevScrollHeight,
  ]);
};

function mergeRefs<T>(
  ...refs: Array<React.Ref<T> | undefined>
): React.RefCallback<T> {
  return (element: T) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T | null>).current = element;
      }
    });
  };
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    { className, children, infiniteScrollRef, observeResize = false, ...props },
    ref
  ) => {
    const internalViewportRef = React.useRef<HTMLDivElement>(null);
    const childrenCount = React.Children.count(children);

    useResizeObserver(
      internalViewportRef,
      childrenCount,
      children,
      observeResize
    );

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        className={cn('relative overflow-hidden', className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={mergeRefs(internalViewportRef, infiniteScrollRef)}
          className="h-full w-full rounded-[inherit]"
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  }
);

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' &&
        'h-2.5 border-t border-t-transparent p-[1px]',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
